/**
 * Pilcore PMS - Child Guest Create Customer Popup
 * Same UX as Booker/MainGuest/Accompanying popups, but assigns customer to reservation_draft_guests as child.
 */

import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import GuestProfileDialog from "./GuestProfileDialog";

const ChildCustomerCreatePopup = ({
  show,
  businessUnitId,
  reservationDraftId,
  reservationId = null,
  guestIndex, // ✅ REQUIRED on create (slot 0..maxChildren-1)
  onClose,
  mode = "create", // "create" | "edit"
  initialData = null,
  onSaved,
}) => {
  const [contentReady, setContentReady] = useState(false);
  const [requiredFields, setRequiredFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const busyRef = useRef(false);

  const [formData, setFormData] = useState({
    salutation: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    second_last_name: "",
    gender: "",
    birth_date: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    address: "",
    postal_code: "",
    document_type: "",
    document_number: "",
    is_vip: false,
    vip_level: "",
    customer_description: "",
    photo_url: "",
    status: "active",
  });

  useEffect(() => {
    if (!show) return;

    if (mode === "edit" && initialData) {
      console.log("[CHILD_POPUP][INIT][EDIT] loading initialData", { id: initialData.id });
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        document_type: initialData.document_type ?? "",
        document_number: initialData.document_number ?? "",
        salutation: initialData.salutation ?? "",
        gender: initialData.gender ?? "",
        vip_level: initialData.vip_level ?? "",
        birth_date: initialData.birth_date ?? "",
      }));
    }

    if (mode === "create") {
      console.log("[CHILD_POPUP][INIT][CREATE] reset empty form");
      setFormData({
        salutation: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        second_last_name: "",
        gender: "",
        birth_date: "",
        email: "",
        phone: "",
        country: "",
        city: "",
        address: "",
        postal_code: "",
        document_type: "",
        document_number: "",
        is_vip: false,
        vip_level: "",
        customer_description: "",
        photo_url: "",
        status: "active",
      });
    }
  }, [show, mode, initialData]);

  useEffect(() => {
    if (!show) return;

    setContentReady(false);
    setFeedback("");

    async function loadEverything() {
      try {
        console.log("[ChildCreatePopup][LOAD] hotel settings", { businessUnitId });
        const resolvedBusinessUnitId = businessUnitId || initialData?.business_unit_id || null;

        if (!resolvedBusinessUnitId) {
          console.warn("[ChildCreatePopup][LOAD] Missing businessUnitId; required fields not loaded.");
          setRequiredFields([]);
          return;
        }

        setFormData((prev) => ({
          ...prev,
          business_unit_id: resolvedBusinessUnitId,
        }));

        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
          params: { business_unit_id: resolvedBusinessUnitId },
        });

        const raw = res.data?.required_customer_fields_reservation ?? null;

        let parsed = null;
        if (typeof raw === "string") {
          try {
            parsed = JSON.parse(raw);
          } catch (e) {
            console.warn("[ChildCreatePopup][SETTINGS] JSON parse failed", { raw });
          }
        } else {
          parsed = raw;
        }

        let rf = [];
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          // ✅ role key for children (accept both "child" or "children")
          rf = Array.isArray(parsed.child)
            ? parsed.child
            : Array.isArray(parsed.children)
              ? parsed.children
              : [];
        } else if (Array.isArray(parsed)) {
          rf = parsed;
        }

        console.log("[ChildCreatePopup][SETTINGS] requiredFields(child)", rf);
        setRequiredFields(rf);
      } catch (err) {
        console.error("[ChildCreatePopup][LOAD][ERR]", err);
        setRequiredFields([]);
      } finally {
        setContentReady(true);
      }
    }

    loadEverything();
  }, [show, businessUnitId, initialData?.business_unit_id]);

  if (!show) return null;
  if (!contentReady) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback("");
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const normalizePayload = () => ({
    ...formData,
    document_type: formData.document_type === "" ? null : formData.document_type,
    document_number: formData.document_number === "" ? null : formData.document_number,
    salutation: formData.salutation === "" ? null : formData.salutation,
    gender: formData.gender === "" ? null : formData.gender,
    vip_level: formData.vip_level === "" ? null : formData.vip_level,
    birth_date: isNaN(new Date(formData.birth_date).getTime()) ? null : formData.birth_date,
  });

  const hasAnyUserInput = () => {
    const keys = [
      "salutation","first_name","middle_name","last_name","second_last_name",
      "gender","birth_date","email","phone","country","city","address","postal_code",
      "document_type","document_number","vip_level","customer_description","photo_url",
    ];

    const anyText = keys.some((k) => {
      const v = formData?.[k];
      return v != null && String(v).trim() !== "";
    });

    return anyText || !!formData?.is_vip;
  };

  const getMissing = () =>
    (requiredFields || []).filter((f) => {
      const v = formData?.[f];
      return v == null || String(v).trim() === "";
    });

  const assignmentTargetId = reservationId || reservationDraftId;
  const usesRealReservation = Boolean(reservationId);

  const assignChildGuest = async (customerId) => {
    if (usesRealReservation) {
      await axios.post("/api/reservation-guests", {
        reservation_id: reservationId,
        customer_id: customerId,
        is_main_guest: false,
        is_child: true,
        relationship: null,
        guest_index: Number(guestIndex),
        updated_by: "ChildPopupCreate",
      });
      return;
    }

    await axios.post("/api/reservation-drafts/draft-guests", {
      reservation_draft_id: reservationDraftId,
      customer_id: customerId,
      is_main_guest: false,
      is_child: true,
      relationship: null,
      guest_index: Number(guestIndex),
      updated_by: "ChildPopupCreate",
    });
  };

  const removeChildGuest = async (customerId) => {
    if (usesRealReservation) {
      await axios.delete(
        `/api/reservation-guests/by-reservation/${reservationId}/by-customer/${customerId}/child`,
        {
          params:
            guestIndex != null && !Number.isNaN(Number(guestIndex))
              ? { guest_index: Number(guestIndex) }
              : {},
        }
      );
      return;
    }

    await axios.delete(
      `/api/reservation-drafts/draft-guests/by-draft/${reservationDraftId}/by-customer/${customerId}/child`,
      {
        params:
          guestIndex != null && !Number.isNaN(Number(guestIndex))
            ? { guest_index: Number(guestIndex) }
            : {},
      }
    );
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (busyRef.current) return;
    setFeedback("");
    console.log("[CHILD_POPUP][SAVE][CLICK]", {
      mode,
      reservationDraftId,
      reservationId,
      businessUnitId,
      guestIndex,
      requiredFields,
    });

    const missing = getMissing();
    if (missing.length > 0) {
      console.warn("[CHILD_POPUP][SAVE][BLOCKED_MISSING]", { missing });
      setFeedback({ key: "Missing required fields: {fields}", fields: missing });
      return;
    }

    if (!assignmentTargetId && mode === "create") {
      setFeedback("Missing reservation id.");
      return;
    }

    if (mode === "create" && (guestIndex == null || Number.isNaN(Number(guestIndex)))) {
      setFeedback("Missing guest slot (guest_index).");
      return;
    }

    const payload = normalizePayload();

    busyRef.current = true;
    setSaving(true);
    try {
      if (mode === "create") {
        const { data: created } = await axios.post("/api/customers", payload);
        const newCustomerId = created?.id;

        console.log("[CHILD_POPUP][SAVE][CREATE_OK]", { newCustomerId });

        if (!newCustomerId) {
          setFeedback("Customer created but no id returned.");
          return;
        }

        await assignChildGuest(newCustomerId);

        console.log("[CHILD_POPUP][SAVE][ASSIGNED_OK]", {
          reservationDraftId,
          reservationId,
          newCustomerId,
          guest_index: Number(guestIndex),
        });
      } else {
        const customerId = initialData?.id;
        if (!customerId) {
          setFeedback("Missing customer id for edit.");
          return;
        }

        await axios.put(`/api/customers/${customerId}`, payload);
        console.log("[CHILD_POPUP][SAVE][EDIT_OK]", { customerId });
      }

      if (typeof onSaved === "function") {
        await onSaved();
      } else if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      console.error("[CHILD_POPUP][SAVE][ERR]", err);
      setFeedback("Error saving child.");
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (busyRef.current) return;
    setFeedback("");
    // ✅ si estás creando o no hay customer todavía (o el form está vacío), Remove = Close
    if (!hasAnyUserInput() || mode === "create" || !initialData?.id) {
      console.log("[CHILD_POPUP][REMOVE][NO_CUSTOMER_CLOSE_OK]", { mode });
      onClose();
      return;
    }

    busyRef.current = true;
    setRemoving(true);
    try {
      const customerId = initialData?.id;
      if (!assignmentTargetId || !customerId) {
        setFeedback("Missing reservation id or customer id.");
        return;
      }

      console.log("[CHILD_POPUP][REMOVE][DELETE] start", {
        reservationDraftId,
        reservationId,
        customerId,
        guestIndex,
      });

      await removeChildGuest(customerId);

      console.log("[CHILD_POPUP][REMOVE][OK]");

      if (typeof onSaved === "function") {
        await onSaved();
      } else if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      console.error("[CHILD_POPUP][REMOVE][ERR]", err);
      setFeedback("Error removing child.");
    } finally {
      busyRef.current = false;
      setRemoving(false);
    }
  };

  return (
    <GuestProfileDialog
      title={mode === "edit" ? "Edit Child Guest" : "Create Child Guest"}
      subtitle={mode === "edit"
        ? "Update the selected child guest details while preserving the current child slot."
        : "Create a new guest profile and assign it directly to the selected child slot."}
      contextLabel={mode === "edit" ? "Child guest details" : "New child guest"}
      createLabel="Create child guest"
      removeLabel="Remove child guest"
      mode={mode}
      formData={formData}
      handleChange={handleChange}
      handleSelectChange={(selected, action) => {
        setFeedback("");
        setFormData((prev) => ({ ...prev, [action.name]: selected ? selected.value : "" }));
      }}
      requiredFields={requiredFields}
      onClose={onClose}
      onSubmit={handleSave}
      onRemove={handleRemove}
      saving={saving}
      removing={removing}
      feedback={feedback}
    />
  );
};

export default ChildCustomerCreatePopup;
